/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import ContinuousUsageStreaksChart from "./ContinuousUsageStreaksChart";
import { formatUTCDate } from "../../utils/utils";
import "../active-users/active-users.css";
import "./ContinuousUsageStreaks.css";

// Guarded decode: atob throws on malformed base64, and an uncaught throw during
// render blanks the page. Mirrors the 042 detail page.
const decodeUserId = (encoded) => {
  try { return atob(encoded || ''); } catch { return ''; }
};

// gap/min ride on the query string so the detail window matches the exact runs the
// listing showed. Absent (e.g. a bookmarked URL) → the feature defaults.
const readParam = (name, fallback) => {
  try {
    const v = new URLSearchParams(window.location.search).get(name);
    return v != null && v !== '' ? v : fallback;
  } catch { return fallback; }
};

// What makes a day "qualifying" under the active mode (047) — must match the listing.
const MODE_RULE = {
  pause_alerts: 'both a heart-rate alert and a pause (meditation or breath)',
  only_pause: 'at least one pause (meditation or breath)',
  only_alerts: 'a heart-rate alert',
};

const InfoItem = ({ label, value, mono }) => (
  <div className="cus-info__item">
    <span className="cus-info__label">{label}</span>
    <span className={`cus-info__value${mono ? ' tnum' : ''}`}>{value || '-'}</span>
  </div>
);

const ContinuousUsageStreaksDetail = () => {
  const navigate = useNavigate();
  const { encodedUserId } = useParams();
  const userId = decodeUserId(encodedUserId);
  const gap = readParam('gap', '4');
  const min = readParam('min', '42');
  // Carried from the listing so the drill-down applies the SAME qualifying-day rule (047).
  const mode = readParam('mode', 'pause_alerts');
  const modeRule = MODE_RULE[mode] || MODE_RULE.pause_alerts;

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [user, setUser] = useState(null);
  const [win, setWin] = useState(null);
  const [days, setDays] = useState([]);
  const [truncated, setTruncated] = useState(false);

  const LIMIT = 40;
  const [page, setPage] = useState(1);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const load = async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/continuous-usage-streaks-detail?user_id=${encodeURIComponent(userId)}` +
        `&gap_tolerance=${encodeURIComponent(gap)}&min_days=${encodeURIComponent(min)}&mode=${encodeURIComponent(mode)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = res.data.data || {};
      setUser(d.user || null);
      setWin(d.window || null);
      setDays(d.days || []);
      setTruncated(!!d.truncated);
      setPage(1);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(days.length / LIMIT) || 1;
  const pageDays = days.slice((page - 1) * LIMIT, page * LIMIT);

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Continuous usage streaks › <b>Details</b></span>
            <span className="au-route">/continuous-usage-streaks-details</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/continuous-usage-streaks">← Back</Link></span>
          </div>
          <h1 className="au-title">
            Continuous Usage
            {user?.name || user?.email ? <span className="au-subtitle"> — {user.name || user.email}</span> : null}
          </h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load this user’s detail. Please try again." onRetry={load} />
          ) : loading ? (
            <div className="au-panel"><TableSkeleton rows={6} /></div>
          ) : !win ? (
            <EmptyState
              title="No qualifying run for this user"
              icon="📉"
              message={`This user has no run of at least ${min} qualifying days with gaps no larger than ${gap} day(s), so there is no window to show.`}
              hasFilters={false}
            />
          ) : (
            <>
              <p className="au-range-note cus-scope-note" data-testid="cus-detail-scope-note">
                A <b>qualifying day</b> has {modeRule}. This user’s run window ran{' '}
                <b>{formatUTCDate(win.run_start)}</b> → <b>{formatUTCDate(win.run_end)}</b>{' '}
                (gap ≤ <b>{gap}</b> day(s), min <b>{min}</b> days). Counts below are totals per day.
              </p>

              <div className="au-panel cus-info-panel" data-testid="cus-info">
                <div className="cus-info">
                  <InfoItem label="Email" value={user?.email} />
                  <InfoItem label="Registered" value={formatUTCDate(user?.registration_date)} />
                  <InfoItem label="Last active" value={formatUTCDate(user?.last_active_date)} />
                  <InfoItem label="Last pause" value={formatUTCDate(user?.last_pause_at)} />
                  <InfoItem label="Last alert" value={formatUTCDate(user?.last_alert_at)} />
                  <InfoItem label="Run start" value={formatUTCDate(win.run_start)} />
                  <InfoItem label="Run end" value={formatUTCDate(win.run_end)} />
                  <InfoItem label="Qualifying days" value={win.total_qualifying_days} mono />
                  <InfoItem label="Best run (days)" value={win.max_run_usage_days} mono />
                  <InfoItem label="Window span (days)" value={win.span_days} mono />
                </div>
              </div>

              <ContinuousUsageStreaksChart days={days} truncated={truncated} />

              <div className="au-panel">
                <div className="au-panel__head">
                  <h4 className="au-panel__title">Day-by-day ({formatUTCDate(win.run_start)} → {formatUTCDate(win.run_end)})</h4>
                </div>
                <div className="au-table-wrap">
                  <table className="au-table cus-table" data-testid="cus-detail-table">
                    <thead>
                      <tr>
                        <th>No</th><th>Date</th>
                        <th className="tnum">Total Pauses</th>
                        <th className="tnum">Total Alerts</th>
                        <th>Qualifying</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageDays.map((row, index) => (
                        <tr key={row.date} className={row.qualifying ? '' : 'cus-gap-row'}>
                          <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                          <td data-label="Date">{formatUTCDate(row.date)}</td>
                          <td data-label="Total Pauses" className="tnum">{row.pauses}</td>
                          <td data-label="Total Alerts" className="tnum">{row.alerts}</td>
                          <td data-label="Qualifying">
                            <span className={`cus-badge cus-badge--${row.qualifying ? 'yes' : 'no'}`}>
                              {row.qualifying ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="cus-pager">
                    <button type="button" className="au-btn au-btn--ghost" disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
                    <span className="cus-pager__label">Page {page} of {totalPages}</span>
                    <button type="button" className="au-btn au-btn--ghost" disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next →</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContinuousUsageStreaksDetail;
